<template>
  <v-row>
    <!-- LEFT: FORM -->
    <v-col cols="12" md="6">
      <v-card
        class="inv-form-card d-flex flex-column"
        height="calc(100vh - 82px)"
        flat
      >
        <div class="inv-form-header pl-base-bg">
          <div class="inv-form-header-icon">
            <v-icon color="#fff" size="22">mdi-file-document-outline</v-icon>
          </div>
          <div>
            <div class="inv-form-title">Edit Invoice</div>
            <div class="inv-form-subtitle">{{ form.invoiceNumber ? `Invoice #${form.invoiceNumber}` : "Update invoice details" }}</div>
          </div>
        </div>

        <v-form ref="form" v-model="valid" :disabled="isPaid" class="pa-6 overflow-y-auto flex-grow-1">
          <v-row dense>
            <!-- INVOICE NUMBER -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-text-field
                v-model="form.invoiceNumber"
                label="Invoice Number"
                outlined
                dense
                disabled
                prepend-inner-icon="mdi-pound"
              />
            </v-col>

            <!-- INVOICE DATE -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="invoiceMenu"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.invoiceDate)"
                    label="Invoice Date"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    prepend-inner-icon="mdi-calendar"
                  />
                </template>

                <v-date-picker
                  v-model="form.invoiceDate"
                  @change="invoiceMenu = false"
                />
              </v-menu>
            </v-col>

            <!-- DUE DATE -->
            <v-col :cols="isMobile ? 12 : 4">
              <v-menu
                v-model="dueMenu"
                offset-y
                left
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDate(form.dueDate)"
                    label="Due Date"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    prepend-inner-icon="mdi-calendar"
                    :append-icon="isPaid ? undefined : 'mdi-close-circle'"
                    @click:append="form.dueDate = null"
                  />
                </template>

                <v-date-picker
                  v-model="form.dueDate"
                  @change="dueMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.name"
                label="Customer Name"
                outlined
                dense
                prepend-inner-icon="mdi-account"
                disabled
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.address"
                label="Customer Address"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
                disabled
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.customer.gstin"
                label="Customer GSTIN"
                outlined
                dense
                prepend-inner-icon="mdi-card-account-details"
                disabled
              />
            </v-col>

            <v-col :cols="isMobile ? 12 : 6">
              <v-text-field
                v-model="form.placeOfSupply"
                label="Place of Supply (State)"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker-radius"
                disabled
              />
            </v-col>

            <v-col cols="12">
              <div class="inv-section-label">Items</div>

              <div
                v-for="(item, index) in form.items"
                :key="index"
                class="inv-item-row"
              >
                <v-btn
                  icon
                  class="inv-item-remove"
                  @click="removeItem(index)"
                  :disabled="isPaid"
                >
                  <v-icon color="error" size="24">mdi-delete-outline</v-icon>
                </v-btn>

                <v-row dense>
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-autocomplete
                      v-model="item.productObj"
                      :items="products"
                      item-text="productName"
                      return-object
                      label="Product"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-cube-outline"
                      :disabled="isPaid"
                      @change="onProductSelect(item, index)"
                    >
                      <!-- ADD NEW PRODUCT -->
                      <template v-if="!isPaid" v-slot:append>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              color="primary"
                              style="cursor: pointer"
                              @click="openProductDialog(index)"
                            >
                              mdi-plus
                            </v-icon>
                          </template>
                          <span>New Product</span>
                        </v-tooltip>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model="item.hsn"
                      label="HSN / SAC"
                      dense
                      outlined
                      hide-details
                      hint="4-8 digit HSN/SAC code"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.quantity"
                      type="number"
                      label="Qty"
                      dense
                      outlined
                      :hint="stockHint(item)"
                      :persistent-hint="!!stockHint(item)"
                      :class="{ 'inv-qty-warning': isOverStock(item) }"
                      @input="calcItem(index)"
                    />
                  </v-col>
                </v-row>

                <v-row dense class="mt-1">
                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.price"
                      type="number"
                      label="Rate"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      v-model.number="item.taxPercent"
                      type="number"
                      label="GST %"
                      dense
                      outlined
                      hide-details
                      @input="calcItem(index)"
                    />
                  </v-col>

                  <v-col :cols="isMobile ? 12 : 4">
                    <v-text-field
                      :value="formatAmount(item.amount)"
                      label="Amount"
                      dense
                      outlined
                      readonly
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>

              <v-btn small class="inv-add-item-btn" :disabled="isPaid" @click="addItem">
                <v-icon left size="16">mdi-plus</v-icon>
                Add Product
              </v-btn>
            </v-col>

            <!-- TOTALS -->
            <v-col cols="12">
              <v-divider class="my-4"></v-divider>

              <div class="totals-grid">
                <div>Subtotal</div>
                <div>₹ {{ formatAmount(subtotal) }}</div>

                <div>GST</div>
                <div>₹ {{ formatAmount(gst) }}</div>
              </div>

              <div class="total-row mt-2">
                <span>Total</span>
                <strong>₹ {{ formatAmount(totalAmount) }}</strong>
              </div>
            </v-col>

            <!-- NOTE -->
            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Note"
                outlined
                dense
                rows="2"
                prepend-inner-icon="mdi-note-text"
              />
            </v-col>
          </v-row>
        </v-form>

        <v-row class="inv-form-actions px-6 flex-grow-0 flex-shrink-0" justify="end" dense>
          <v-col v-if="!isViewOnly" cols="4" sm="auto">
            <v-btn
              block
              color="error"
              outlined
              @click="confirmDelete = true"
              :disabled="isPaid"
            >
              Delete
            </v-btn>
          </v-col>

          <v-col cols="4" sm="auto">
            <v-btn block color="secondary" @click="reset">Reset</v-btn>
          </v-col>

          <v-col v-if="!isViewOnly" cols="4" sm="auto">
            <v-tooltip top :disabled="isPaid || pdfGenerated">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <v-btn
                    block
                    class="inv-save-btn"
                    :loading="loading"
                    @click="submit"
                    :disabled="isPaid || !pdfGenerated"
                  >
                    Update
                  </v-btn>
                </div>
              </template>
              <span>Click "Generate Invoice" on the right to refresh the PDF before updating</span>
            </v-tooltip>

            <div v-if="!isPaid && !pdfGenerated" class="inv-update-hint">
              Regenerate the PDF to enable Update
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-col>

    <!-- RIGHT: FILE -->
    <v-col cols="12" md="6">
      <v-card
        class="inv-preview-card d-flex flex-column"
        flat
        height="calc(100vh - 82px)"
      >
        <div class="inv-preview-header">
          <v-icon color="#0a5a90" class="mr-2">mdi-file-document-multiple-outline</v-icon>
          <h3 class="inv-preview-title">Document</h3>
        </div>

        <v-row v-if="!isViewOnly" class="px-4 flex-grow-0 flex-shrink-0" no-gutters>
          <v-col cols="12">
            <v-btn
              block
              class="inv-generate-btn"
              :class="{ 'inv-generate-btn--attn': !pdfGenerated && !isPaid }"
              @click="autoGenerateInvoice"
              :disabled="
                isPaid || totalAmount <= 0 || !form.customer.name || !form.invoiceNumber
              "
            >
              Generate Invoice
            </v-btn>
            <div v-if="!isPaid && !pdfGenerated" class="mt-2 text-caption" style="color: #b71c1c">
              Generate the invoice PDF before updating.
            </div>
          </v-col>
        </v-row>

        <!-- EXISTING FILE -->
        <div v-if="existingDocument && docState === 'keep'" class="mt-2 px-4 flex-shrink-0">
          <v-chip small color="primary">
            {{ existingDocument.fileName }}
          </v-chip>

          <v-btn
            v-if="!isViewOnly"
            small
            color="error"
            outlined
            class="ml-3"
            @click="removeFile"
            :disabled="isPaid"
          >
            Remove
          </v-btn>
        </div>

        <!-- PREVIEW -->
        <div class="viewer mt-4 mx-4">
          <img v-if="preview && isImage" :src="preview" class="preview-img" />

          <iframe v-else-if="preview" :src="preview" class="preview-frame" />

          <iframe
            v-else-if="existingDocument && docState === 'keep'"
            :src="existingDocument.fileUrl"
            class="preview-frame"
          />

          <div v-else class="empty-state">
            <v-icon size="40" color="#cbd5e1">mdi-file-document-outline</v-icon>
            <div class="mt-2">No document selected</div>
          </div>
        </div>
      </v-card>
    </v-col>

    <global-dialog v-model="productDialog" title="New Product" :width="600">
      <create-product mode="dialog" @created="onProductCreated" />
    </global-dialog>

    <!-- DELETE -->
    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this invoice?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";
import CreateProduct from "./create-product.vue";
import { mapActions } from "vuex";
import {
  resolveSellerState,
  resolvePlaceOfSupplyState,
  splitTax,
  numberToWords,
  formatINR,
} from "../utils/gst";

export default {
  components: { GlobalDialog, CreateProduct },

  data() {
    return {
      valid: false,
      loading: false,

      invoiceMenu: false,
      dueMenu: false,

      file: null,
      preview: null,
      pdfGenerated: false,

      existingDocument: null,
      docState: "keep",

      confirmDelete: false,
      invoiceStatus: null,

      productDialog: false,
      activeItemIndex: null,

      form: {
        invoiceNumber: "",
        invoiceDate: "",
        dueDate: "",
        customer: { name: "", address: "", gstin: "", state: "" },
        items: [],
        note: "",
      },
    };
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.pdfGenerated = false;
      },
    },
  },

  computed: {
    subtotal() {
      return this.form.items.reduce((s, i) => s + (i.amount || 0), 0);
    },

    gst() {
      return this.form.items.reduce(
        (s, i) => s + ((i.amount || 0) * (i.taxPercent || 0)) / 100,
        0,
      );
    },

    totalAmount() {
      return this.subtotal + this.gst;
    },

    sellerState() {
      return resolveSellerState({
        sellerGstin: this.profile?.gstin,
        sellerState: this.profile?.state,
      });
    },

    placeOfSupplyState() {
      return resolvePlaceOfSupplyState({
        customerGstin: this.form.customer.gstin,
        customerState: this.form.customer.state,
        explicitPlaceOfSupply: this.form.placeOfSupply,
      });
    },

    taxSplit() {
      return this.form.items.reduce(
        (acc, item) => {
          const { isInterState, cgst, sgst, igst } = splitTax({
            sellerState: this.sellerState,
            placeOfSupplyState: this.placeOfSupplyState,
            taxableAmount: item.amount || 0,
            taxPercent: item.taxPercent || 0,
          });
          acc.isInterState = isInterState;
          acc.cgst += cgst;
          acc.sgst += sgst;
          acc.igst += igst;
          return acc;
        },
        { isInterState: false, cgst: 0, sgst: 0, igst: 0 },
      );
    },

    isPaid() {
      return this.invoiceStatus === "PAID";
    },

    isImage() {
      return this.file?.type?.startsWith("image/");
    },

    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        { text: "Delete", action: this.delete, color: "error" },
      ];
    },

    profile() {
      return this.$store.state.business?.profile;
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    terms() {
      return this.$store.state.terms.terms;
    },

    bankAccount() {
      return this.$store.state.bank.account;
    },

    products() {
      return this.$store.state.products.products;
    },
  },

  async created() {
    this.fetchProducts();
    await this.loadInvoice();
  },

  methods: {
    ...mapActions("business", ["getProfile"]),
    formatDate(d) {
      if (!d) return "";
      const date = new Date(d);
      return `${String(date.getDate()).padStart(2, "0")}/${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}/${date.getFullYear()}`;
    },

    formatAmount(v) {
      return Number(v || 0).toFixed(2);
    },

    addItem() {
      this.form.items.push({
        productId: null,
        product: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
        availableStock: null,
      });
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;

      item.amount = +(qty * price).toFixed(2);
    },

    async fetchProducts() {
      try {
        await this.$store.dispatch("products/getProducts");
      } catch (e) {
        // ignore — product dropdown just stays empty
      }
    },

    openProductDialog(index) {
      this.activeItemIndex = index;
      this.productDialog = true;
    },

    onProductSelect(item, index) {
      const product = item.productObj;

      if (!product) return;

      item.productId = product._id;
      item.product = product.productName;
      item.hsn = product.hsn;
      item.price = product.rate;
      item.availableStock = product.trackStock ? product.stockQuantity : null;

      this.calcItem(index);
    },

    stockHint(item) {
      if (item.availableStock == null) return "";
      return this.isOverStock(item)
        ? `Only ${item.availableStock} in stock`
        : `${item.availableStock} in stock`;
    },

    isOverStock(item) {
      return (
        item.availableStock != null &&
        Number(item.quantity) > item.availableStock
      );
    },

    async onProductCreated(product) {
      this.productDialog = false;

      if (!product) return;

      const index = this.activeItemIndex;

      if (index === null || !this.form.items[index]) return;

      await this.fetchProducts();

      const item = this.form.items[index];

      const selected = this.products.find((p) => p._id === product._id);

      const finalProduct = selected || product;

      this.$set(item, "productObj", finalProduct);

      this.onProductSelect(item, index);

      this.activeItemIndex = null;
    },

    async loadInvoice() {
      const id = this.$route.params.id;
      const res = await this.$store.dispatch("invoices/getInvoiceById", id);

      this.form = {
        ...res.invoice,
        dueDate: res.invoice.dueDate
          ? new Date(res.invoice.dueDate).toLocaleDateString("en-CA", {
              timeZone: "Asia/Kolkata",
            })
          : "",

        invoiceDate: res.invoice.invoiceDate
          ? new Date(res.invoice.invoiceDate).toLocaleDateString("en-CA", {
              timeZone: "Asia/Kolkata",
            })
          : "",

        // Seed the dropdown display with the saved product snapshot — a
        // real catalog match (if any) is attached once the product list
        // loads. Carrying productId through lets re-selection/display work
        // for invoices that already link to a catalog product.
        items: (res.invoice.items || []).map((item) => ({
          ...item,
          availableStock: null,
          productObj: item.product
            ? { productName: item.product, _id: item.productId }
            : null,
        })),
      };

      this.invoiceStatus = res.invoice.status;

      this.existingDocument = res.document || null;
      this.preview = res.document?.fileUrl || null;

      this.file = null;
      this.docState = "keep";
    },

    removeFile() {
      this.file = null;
      this.preview = null;
      this.docState = "remove";
      this.pdfGenerated = false;
    },

    async submit() {
      this.loading = true;

      try {
        let fileBase64 = null;

        if (this.file) {
          fileBase64 = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsDataURL(this.file);
          });
        }

        const result = await this.$store.dispatch("invoices/UpdateInvoice", {
          id: this.$route.params.id,
          ...this.form,
          gst: this.gst,
          cgst: this.taxSplit.cgst,
          sgst: this.taxSplit.sgst,
          igst: this.taxSplit.igst,
          isInterState: this.taxSplit.isInterState,
          totalAmount: this.totalAmount,
          file: fileBase64,
          fileName: this.file?.name || null,
          fileType: this.file?.type || null,
          existingDocumentId: this.existingDocument?._id || null,
          removeFile: this.docState === "remove",
        });

        this.$toast.success("Updated");

        (result?.stockWarnings || []).forEach((w) => {
          this.$toast.warning(
            `${w.productName} stock is now ${w.resultingQuantity} (below zero)`,
          );
        });

        this.$router.push(`/invoices/${this.form._id}/view`);
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.loadInvoice();
    },

    async delete() {
      await this.$store.dispatch(
        "invoices/DeleteInvoice",
        this.$route.params.id,
      );
      this.$router.push("/invoices");
    },

    removeItem(index) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(index, 1);
    },

    formatINR(value) {
      return formatINR(value);
    },

    wrapText(doc, text, x, y, maxWidth, lineHeight = 5) {
      const lines = doc.splitTextToSize(text || "-", maxWidth);
      lines.forEach((line, i) => {
        doc.text(line, x, y + i * lineHeight);
      });
      return lines.length * lineHeight;
    },

    numberToWords(num) {
      return numberToWords(num);
    },

    async autoGenerateInvoice() {
      try {
        await Promise.all([
          this.getProfile(),
          this.$store.dispatch("terms/getTerms"),
          this.$store.dispatch("bank/getAccount"),
        ]);

        const customer   = this.form.customer;
        const bank       = this.bankAccount;
        const termsRaw   = this.$store.state.terms?.terms?.content || "Goods once sold will not be taken back.\nPay on time.";

        if (!this.profile || !customer) {
          this.$toast.error("Seller or Customer missing");
          return;
        }

        const seller = {
          ...this.profile,
          address: [
            this.profile.address,
            this.profile.city,
            this.profile.state,
            this.profile.pincode,
            this.profile.country,
          ]
            .filter(Boolean)
            .join(", "),
        };

        const { jsPDF } = await import("jspdf");
        const autoTable = (await import("jspdf-autotable")).default;
        const doc       = new jsPDF({ unit: "mm", format: "a4" });
        const pageW     = doc.internal.pageSize.width;
        const pageH     = doc.internal.pageSize.height;
        const M         = 0;
        const CW        = pageW;
        const PAD       = 5;
        const LH        = 4.5;
        const FOOTER_H  = 10;

        const NAVY   = [22,  40,  74];
        const BLUE   = [47,  95, 208];
        const MUTED  = [138, 147, 166];
        const BODY   = [82,  92, 110];
        const LBODY  = [107, 116, 136];
        const WHITE  = [255, 255, 255];
        const DARK   = [26,  34,  51];
        const BGROW  = [247, 249, 252];
        const BGALT  = [248, 250, 255];
        const BRDR   = [227, 232, 239];
        const BLIGHT = [238, 241, 246];
        const WBLU   = [238, 243, 251];
        const WBDR   = [221, 231, 247];

        const sc  = (...rgb) => doc.setTextColor(...rgb);
        const rc  = ()       => sc(...DARK);
        const fr  = (x, y, w, h, rgb) => {
          doc.setFillColor(...rgb); doc.setDrawColor(...rgb);
          doc.rect(x, y, w, h, "FD");
        };
        const sr  = (x, y, w, h, rgb = BRDR) => {
          doc.setDrawColor(...rgb); doc.setLineWidth(0.3);
          doc.rect(x, y, w, h, "S");
        };
        const hl  = (x1, x2, yy, rgb = BLIGHT) => {
          doc.setDrawColor(...rgb); doc.setLineWidth(0.25);
          doc.line(x1, yy, x2, yy);
        };
        const rl  = (lines, x, startY, sz, style, color, lh = LH) => {
          doc.setFontSize(sz); doc.setFont("helvetica", style);
          if (color) sc(...color); else rc();
          lines.forEach((l, i) => doc.text(l, x, startY + i * lh));
          rc();
          return startY + lines.length * lh;
        };
        const wt  = (text, maxW) =>
          doc.splitTextToSize(text ? String(text) : "-", maxW);

        const fmtDate = (d) => {
          if (!d) return "-";
          const [yr, mo, dy] = d.split("-");
          return `${dy}/${mo}/${yr}`;
        };

        const sameState = !this.taxSplit.isInterState;

        const termEntries = termsRaw
          .split(/\n/)
          .map(l => l.trim().replace(/^\d+[.)]\s*/, ""))
          .filter(Boolean);

        let y = M;

        // ── 1. HEADER ──────────────────────────────────────────
        const badgeW  = 52;
        const badgeH  = 12;
        const badgeX  = M + CW - badgeW;
        const badgeY  = y;
        const leftW   = badgeX - M - PAD - 4;
        const addrWrap    = wt(seller.address || "", leftW);
        const contactStr  = [seller.phone || "", seller.email || ""].filter(Boolean).join("   ·   ");
        const contactWrap = wt(contactStr, leftW);
        const headerH     = Math.max(32, 8 + 7 + 3 + addrWrap.length * 3.8 + 2 + contactWrap.length * 3.8 + 4);

        fr(M, y, CW, headerH, NAVY);
        doc.setFontSize(16); doc.setFont("helvetica", "bold"); sc(...WHITE);
        doc.text(seller.businessName || "Company Name", M + PAD + 2, y + 10);

        let hSubY = y + 16;
        hSubY = rl(addrWrap,    M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);
        hSubY += 2;
        rl(contactWrap, M + PAD + 2, hSubY, 7.5, "normal", [169, 183, 208], 3.8);

        fr(badgeX, badgeY, badgeW, badgeH, BLUE);
        doc.setFontSize(9); doc.setFont("helvetica", "bold"); sc(...WHITE);
        doc.text("TAX INVOICE", badgeX + badgeW / 2, badgeY + badgeH / 2 + 1.8, { align: "center", charSpace: 0.8 });

        if (seller.gstin) {
          doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); sc(169, 183, 208);
          doc.text(`GSTIN  ${seller.gstin}`, M + CW - 2, badgeY + badgeH + 6, { align: "right" });
        }
        rc();
        y += headerH;

        // ── 2. META ROW ─────────────────────────────────────────
        const infoH = 18;
        fr(M, y, CW, infoH, BGROW);
        doc.setDrawColor(...BRDR); doc.setLineWidth(0.3);
        doc.line(M, y + infoH, M + CW, y + infoH);

        const infoFields = [
          { label: "INVOICE NO",      value: this.form.invoiceNumber || "-" },
          { label: "INVOICE DATE",    value: fmtDate(this.form.invoiceDate) },
          { label: "DUE DATE",        value: fmtDate(this.form.dueDate) },
          { label: "PLACE OF SUPPLY", value: this.form.placeOfSupply || "-" },
        ];
        const fw = CW / 4;
        infoFields.forEach((f, i) => {
          const fx = M + i * fw + (i === 0 ? 6 : 5);
          if (i > 0) {
            doc.setDrawColor(...BRDR); doc.setLineWidth(0.25);
            doc.line(M + i * fw, y + 2, M + i * fw, y + infoH - 2);
          }
          doc.setFontSize(6.5); doc.setFont("helvetica", "bold"); sc(...MUTED);
          doc.text(f.label, fx, y + 6.5);
          doc.setFontSize(9); doc.setFont("helvetica", "bold"); sc(...DARK);
          doc.text(String(f.value), fx, y + 14);
        });
        rc();
        y += infoH + 5;

        // ── 3. BILL FROM / BILL TO ──────────────────────────────
        const colW  = (CW - 4) / 2;
        const LHDR  = 7;
        const CGAP  = 1.8;

        const sLines = [
          wt(seller.businessName || "-",          colW - PAD * 2),
          wt(seller.address      || "-",          colW - PAD * 2),
          wt(`Ph  ${seller.phone || "-"}`,        colW - PAD * 2),
          wt(seller.email        || "-",          colW - PAD * 2),
          wt(`GSTIN  ${seller.gstin || "-"}`,     colW - PAD * 2),
        ];
        const cLines = [
          wt(customer.name    || "-",             colW - PAD * 2),
          wt(customer.address || "-",             colW - PAD * 2),
          wt(`GSTIN  ${customer.gstin || "URP"}`, colW - PAD * 2),
        ];

        const totalTextH = (groups) => groups.reduce((s, g) => s + g.length * LH + CGAP, 0);
        const fromH = LHDR + PAD + 1 + totalTextH(sLines) + PAD;
        const toH   = LHDR + PAD + 1 + totalTextH(cLines) + PAD;
        const billH = Math.max(fromH, toH);

        const fromX = M;
        const toX   = M + colW + 4;

        fr(fromX, y, colW, billH, WHITE);
        sr(fromX, y, colW, billH);
        fr(fromX, y, colW, LHDR, BGROW);
        hl(fromX, fromX + colW, y + LHDR, BLIGHT);

        fr(toX, y, colW, billH, WHITE);
        sr(toX, y, colW, billH);
        fr(toX, y, colW, LHDR, BGROW);
        hl(toX, toX + colW, y + LHDR, BLIGHT);

        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("BILL FROM", fromX + PAD, y + LHDR - 2);
        doc.text("BILL TO",   toX   + PAD, y + LHDR - 2);
        rc();

        const selStyles = [
          { sz: 9,   style: "bold",   col: DARK },
          { sz: 7.5, style: "normal", col: BODY },
          { sz: 7,   style: "normal", col: LBODY },
          { sz: 7,   style: "normal", col: LBODY },
          { sz: 7.5, style: "normal", col: DARK },
        ];
        let fy = y + LHDR + PAD + 1;
        sLines.forEach((lines, i) => {
          const s = selStyles[i] || { sz: 7.5, style: "normal", col: BODY };
          fy = rl(lines, fromX + PAD, fy, s.sz, s.style, s.col) + CGAP;
        });

        const cusStyles = [
          { sz: 9,   style: "bold",   col: DARK },
          { sz: 7.5, style: "normal", col: BODY },
          { sz: 7.5, style: "normal", col: DARK },
        ];
        let cy2 = y + LHDR + PAD + 1;
        cLines.forEach((lines, i) => {
          const s = cusStyles[i] || { sz: 7.5, style: "normal", col: BODY };
          cy2 = rl(lines, toX + PAD, cy2, s.sz, s.style, s.col) + CGAP;
        });

        y += billH + 5;

        // ── 4. ITEMS TABLE ──────────────────────────────────────
        const tHead = sameState
          ? ["#", "PRODUCT / DESCRIPTION", "HSN/SAC", "QTY", "RATE", "GST%", "CGST", "SGST", "AMOUNT"]
          : ["#", "PRODUCT / DESCRIPTION", "HSN/SAC", "QTY", "RATE", "GST%", "IGST", "AMOUNT"];

        const tBody = this.form.items.map((item, idx) => {
          const { cgst, sgst, igst } = splitTax({
            sellerState: this.sellerState,
            placeOfSupplyState: this.placeOfSupplyState,
            taxableAmount: item.amount || 0,
            taxPercent: item.taxPercent || 0,
          });
          const tax = cgst + sgst + igst;
          const row  = [idx + 1, item.product || "-", item.hsn || "-", item.quantity, this.formatINR(item.price), `${item.taxPercent || 0}%`];
          if (sameState) row.push(this.formatINR(cgst), this.formatINR(sgst));
          else           row.push(this.formatINR(igst));
          row.push(this.formatINR((item.amount || 0) + tax));
          return row;
        });

        const tColStyles = sameState
          ? [
              { halign: "center", cellWidth: 8 },   // #
              { halign: "left" },                    // Product (auto)
              { halign: "center", cellWidth: 22 },   // HSN/SAC
              { halign: "center", cellWidth: 14 },   // Qty
              { halign: "right",  cellWidth: 20 },   // Rate
              { halign: "center", cellWidth: 18 },   // GST%
              { halign: "right",  cellWidth: 18 },   // CGST
              { halign: "right",  cellWidth: 18 },   // SGST
              { halign: "right",  cellWidth: 22 },   // Amount
            ]
          : [
              { halign: "center", cellWidth: 8 },    // #
              { halign: "left" },                    // Product (auto)
              { halign: "center", cellWidth: 22 },   // HSN/SAC
              { halign: "center", cellWidth: 14 },   // Qty
              { halign: "right",  cellWidth: 24 },   // Rate
              { halign: "center", cellWidth: 18 },   // GST%
              { halign: "right",  cellWidth: 26 },   // GST
              { halign: "right",  cellWidth: 28 },   // Amount
            ];

        autoTable(doc, {
          startY: y,
          margin: { left: M, right: M },
          head: [tHead],
          body: tBody,
          theme: "grid",
          styles:             { fontSize: 8.5, overflow: "linebreak", cellPadding: 3, valign: "middle", textColor: DARK },
          headStyles:         { fillColor: NAVY, textColor: WHITE, fontStyle: "bold", fontSize: 7.5, halign: "center", cellPadding: 3.5 },
          alternateRowStyles: { fillColor: BGALT },
          bodyStyles:         { lineColor: BLIGHT, lineWidth: 0.25 },
          columnStyles:       Object.fromEntries(tColStyles.map((s, i) => [i, s])),
          didDrawPage:        (data) => { y = data.cursor.y + 4; },
          showHead:           "everyPage",
        });

        // ── 5. BANK + SUMMARY ───────────────────────────────────
        const grandTotal = this.totalAmount;

        const totRows = sameState
          ? [["Subtotal", this.formatINR(this.subtotal)], ["CGST", this.formatINR(this.taxSplit.cgst)], ["SGST", this.formatINR(this.taxSplit.sgst)]]
          : [["Subtotal", this.formatINR(this.subtotal)], ["IGST", this.formatINR(this.taxSplit.igst)]];

        const TOT_W    = 80;
        const BANK_W   = CW - TOT_W - 4;
        const TOT_X    = M + CW - TOT_W;
        const BANK_X   = M;
        const ROW_H    = 7;
        const GRAND_H  = 10;

        const bankRows = [
          ["Account No", bank?.accountNumber || "-"],
          ["Bank",       bank?.bankName      || "-"],
          ["IFSC",       bank?.ifsc          || "-"],
          ["Branch",     bank?.branch        || "-"],
        ].filter(r => r[1] !== "-" || r[0] === "Account No");

        const sumH     = PAD + 2 + totRows.length * ROW_H + 2 + GRAND_H;
        const bankH    = LHDR + PAD + bankRows.length * ROW_H + PAD + 2;
        const sectionH = Math.max(sumH, bankH, 36);

        if (y + sectionH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }

        fr(BANK_X, y, BANK_W, sectionH, WHITE);
        sr(BANK_X, y, BANK_W, sectionH);
        fr(BANK_X, y, BANK_W, LHDR, BGROW);
        hl(BANK_X, BANK_X + BANK_W, y + LHDR, BLIGHT);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("BANK DETAILS", BANK_X + PAD, y + LHDR - 2);
        rc();

        let bankY = y + LHDR + PAD + 1;
        bankRows.forEach(([lbl, val], bi) => {
          if (bi > 0) hl(BANK_X + PAD, BANK_X + BANK_W - PAD, bankY - 1, BLIGHT);
          doc.setFontSize(7); doc.setFont("helvetica", "normal"); sc(...MUTED);
          doc.text(lbl, BANK_X + PAD, bankY + 2.5);
          doc.setFontSize(8.5); doc.setFont("helvetica", "bold"); sc(...DARK);
          doc.text(String(val), BANK_X + BANK_W - PAD, bankY + 2.5, { align: "right" });
          bankY += ROW_H;
        });
        rc();

        fr(TOT_X, y, TOT_W, sectionH, WHITE);
        sr(TOT_X, y, TOT_W, sectionH);

        let sumY = y + PAD + 2;
        totRows.forEach(([lbl, val], si) => {
          if (si > 0) hl(TOT_X + PAD, TOT_X + TOT_W - PAD, sumY - 1.5, BLIGHT);
          doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); sc(...BODY);
          doc.text(lbl, TOT_X + PAD, sumY + 2.5);
          doc.setFont("helvetica", "normal"); sc(...DARK);
          doc.text(val, TOT_X + TOT_W - PAD, sumY + 2.5, { align: "right" });
          sumY += ROW_H;
        });

        const totalBarY = y + sectionH - GRAND_H;
        fr(TOT_X, totalBarY, TOT_W, GRAND_H, NAVY);
        doc.setFontSize(9); doc.setFont("helvetica", "bold"); sc(...WHITE);
        doc.text("TOTAL", TOT_X + PAD, totalBarY + GRAND_H / 2 + 1.8);
        doc.text(`INR ${this.formatINR(grandTotal)}`, TOT_X + TOT_W - PAD, totalBarY + GRAND_H / 2 + 1.8, { align: "right" });
        rc();

        y += sectionH + 4;

        // ── 6. AMOUNT IN WORDS ──────────────────────────────────
        const wordMaxW  = CW - PAD * 2 - 34;
        const wordsWrap = wt(this.numberToWords(Math.round(grandTotal)), wordMaxW);
        const wordsH    = Math.max(wordsWrap.length * 4.5, 4.5) + 9;

        if (y + wordsH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }

        fr(M, y, CW, wordsH, WBLU);
        sr(M, y, CW, wordsH, WBDR);
        const wordsTextY = y + 7;
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(90, 112, 153);
        doc.text("AMOUNT IN WORDS", M + PAD, wordsTextY);
        doc.setFontSize(8.5); doc.setFont("helvetica", "bolditalic"); sc(...DARK);
        wordsWrap.forEach((l, i) => doc.text(l, M + PAD + 38, wordsTextY + i * 4.5));
        rc();
        y += wordsH + 4;

        // ── 7. NOTES + TERMS ────────────────────────────────────
        const noteW  = (CW - 4) / 2;
        const termW  = (CW - 4) / 2;
        const noteX  = M;
        const termX  = M + noteW + 4;

        const noteWrap     = wt(this.form.note || "-", noteW - PAD * 2);
        const termTextW    = termW - PAD * 2 - 6;
        const termRendered = termEntries.map(e => wt(e, termTextW));
        const termTotalLines = termRendered.reduce((s, l) => s + l.length, 0);
        const ntBodyH = Math.max(noteWrap.length * 4.5, termTotalLines * 5, 10);
        const ntH     = LHDR + PAD + ntBodyH + PAD;

        if (y + ntH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }

        fr(noteX, y, noteW, ntH, WHITE);
        sr(noteX, y, noteW, ntH);
        fr(noteX, y, noteW, LHDR, BGROW);
        hl(noteX, noteX + noteW, y + LHDR, BLIGHT);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("NOTES", noteX + PAD, y + LHDR - 2);
        rl(noteWrap, noteX + PAD, y + LHDR + PAD + 1, 8, "normal", BODY, 4.5);

        fr(termX, y, termW, ntH, WHITE);
        sr(termX, y, termW, ntH);
        fr(termX, y, termW, LHDR, BGROW);
        hl(termX, termX + termW, y + LHDR, BLIGHT);
        doc.setFontSize(7); doc.setFont("helvetica", "bold"); sc(...BLUE);
        doc.text("TERMS & CONDITIONS", termX + PAD, y + LHDR - 2);
        rc();

        let tcy = y + LHDR + PAD + 3;
        termRendered.forEach((lines, ti) => {
          doc.setFontSize(8); doc.setFont("helvetica", "bold"); sc(...BLUE);
          doc.text(`${ti + 1}.`, termX + PAD, tcy);
          doc.setFont("helvetica", "normal"); sc(...BODY);
          lines.forEach((l, li) => doc.text(l, termX + PAD + 6, tcy + li * 4.5));
          tcy += lines.length * 4.5 + 2;
          rc();
        });

        y += ntH + 5;

        // ── 8. SIGNATURE ────────────────────────────────────────
        const sigW  = 66;
        const sigH  = 26;
        const sigX  = M + CW - sigW;
        if (y + sigH > pageH - FOOTER_H - 4) { doc.addPage(); y = M; }
        const sigY  = y;

        fr(sigX, sigY, sigW, sigH, WHITE);
        sr(sigX, sigY, sigW, sigH);
        hl(sigX, sigX + sigW, sigY + sigH - 9, BRDR);

        doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); sc(...MUTED);
        doc.text(`For, ${seller.businessName || "-"}`, sigX + 5, sigY + 7);
        doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); sc(...NAVY);
        doc.text("AUTHORISED SIGNATORY", sigX + sigW / 2, sigY + sigH - 2.5, { align: "center" });
        rc();

        // ── 9. FOOTER ───────────────────────────────────────────
        const nPages = doc.internal.getNumberOfPages();
        for (let p = 1; p <= nPages; p++) {
          doc.setPage(p);
          fr(M, pageH - FOOTER_H, CW, FOOTER_H, NAVY);
          doc.setFontSize(7); doc.setFont("helvetica", "normal"); sc(...WHITE);
          doc.text("This is a computer generated invoice.", M + PAD, pageH - FOOTER_H + 6);
          doc.text(`Page ${p} of ${nPages}`, M + CW - PAD, pageH - FOOTER_H + 6, { align: "right" });
        }
        rc();

        const blob = doc.output("blob");
        this.preview = URL.createObjectURL(blob);
        this.file   = new File([blob], `invoice-${this.form.invoiceNumber}.pdf`, { type: "application/pdf" });
        this.docState = "replace";
        this.pdfGenerated = true;
        this.$toast.success("Invoice Generated Successfully");
      } catch (e) {
        console.error(e);
        this.$toast.error("Invoice generation failed");
      }
    },
  },
};
</script>

<style scoped>
.inv-qty-warning ::v-deep .v-messages__message {
  color: #b23a3a;
}

.inv-form-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.inv-form-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  position: sticky;
  top: 0;
  z-index: 2;
}

.inv-form-actions {
  margin: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: 1px solid #e5e9ef;
  background: #fff;
}

.inv-form-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.inv-form-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.inv-form-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.inv-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.inv-item-row {
  position: relative;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 56px 14px 12px;
  margin-bottom: 10px;
}

.inv-item-remove {
  position: absolute;
  bottom: 6px;
  right: 6px;
}

.inv-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-preview-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.inv-preview-header {
  display: flex;
  align-items: center;
  padding: 18px 20px 8px;
  flex-shrink: 0;
}

.inv-preview-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.inv-generate-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.inv-generate-btn--attn {
  animation: inv-generate-pulse 1.6s ease-in-out infinite;
}

@keyframes inv-generate-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(10, 90, 144, 0.4) !important;
  }
  50% {
    box-shadow: 0 0 0 6px rgba(10, 90, 144, 0) !important;
  }
}

.inv-update-hint {
  font-size: 10.5px;
  color: #b71c1c;
  text-align: center;
  margin-top: 4px;
  line-height: 1.3;
}

.viewer {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 0;
  padding: 10px;
  background: #fafbfc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-img {
  max-width: 100%;
}

.preview-frame {
  width: 100%;
  height: 100%;
  flex: 1;
}

.empty-state {
  text-align: center;
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.totals-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  font-size: 18px;
  border-top: 1px solid #ddd;
  padding-top: 12px;
  margin-top: 10px;
}
</style>
